/**
 * Maersk Logistics API Service
 * Handles authentication, quotations, schedules, and tracking.
 */

import { API_BASE_URL } from '../shared/constants/env';

export interface MaerskQuoteRequest {
    origin: { city: string; countryCode: string };
    destination: { city: string; countryCode: string };
    containers: Array<{ type: string; quantity: number }>;
    cargoType: string;
    weight: number;
}

export interface MaerskTrackingResponse {
    shipmentReference: string;
    milestones: Array<{
        eventCode: string;
        description: string;
        location: string;
        timestamp: string;
    }>;
}

class MaerskService {
    private getApiOrigin(): string {
        return API_BASE_URL.replace(/\/graphql\/?$/, '');
    }

    private async postJson<T>(path: string, payload: unknown): Promise<T> {
        const response = await fetch(`${this.getApiOrigin()}${path}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        let data: any = null;
        try {
            data = await response.json();
        } catch {
            // Keep null payload for non-JSON responses.
        }

        if (!response.ok) {
            const detail = data?.detail || data?.message || `Request failed (${response.status})`;
            throw new Error(detail);
        }

        return data as T;
    }

    /**
     * Fetch shipping rates from Maersk
     */
    async getQuotes(request: MaerskQuoteRequest) {
        const data = await this.postJson<any>('/api/v1/logistics/maersk/quotes', request);
        const quotes = Array.isArray(data) ? data : data?.quotes;
        if (!Array.isArray(quotes)) {
            throw new Error('Maersk quotes API returned an invalid response');
        }
        return quotes;
    }

    /**
     * Track a shipment
     */
    async trackShipment(reference: string): Promise<MaerskTrackingResponse> {
        return this.postJson<MaerskTrackingResponse>('/api/v1/logistics/maersk/track', {
            reference,
        });
    }

    /**
     * Get sailing schedules
     */
    async getSchedules(origin: string, destination: string) {
        const data = await this.postJson<any>('/api/v1/logistics/maersk/schedules', {
            origin,
            destination,
        });
        const schedules = Array.isArray(data) ? data : data?.schedules;
        if (!Array.isArray(schedules)) {
            throw new Error('Maersk schedules API returned an invalid response');
        }
        return schedules;
    }
}

export const maerskService = new MaerskService();
